import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, lineOaApi } from '../../api';
import type { LineOaConnectionDto, LineSalesReportDto, LineSalesReportField, LineSalesReportPreviewDto, LineSalesReportUsageDto } from '../../api';
import { Alert, Button, Card, TextField, Toggle } from '..';

const FIELD_OPTIONS: { id: LineSalesReportField; label: string; detail: string }[] = [
  { id: 'revenue_total', label: 'รายได้ชำระแล้วสะสม', detail: 'รวมเฉพาะออเดอร์ที่สถานะ paid' },
  { id: 'revenue_today', label: 'รายได้วันนี้', detail: 'นับตามวันไทยจาก verified_at' },
  { id: 'orders_today', label: 'จำนวนออเดอร์ที่ชำระวันนี้', detail: 'หน่วยเป็นออเดอร์ ไม่ใช่จำนวนคน' },
  { id: 'tickets_sold_capacity', label: 'บัตรขายแล้ว / ความจุรวม', detail: 'จำนวน qty จากออเดอร์ paid เทียบกับผลรวม capacity ที่ตั้งไว้ทุกประเภท ไม่ตีความเป็นจำนวนคน' },
  { id: 'ticket_breakdown', label: 'รายได้แยกตามประเภทบัตร', detail: 'หลังเฉลี่ยส่วนลดตามสัดส่วนราคา และยอดรวมตรงกับยอดชำระจริง' },
  { id: 'trend_7d', label: 'แนวโน้มรายได้ 7 วัน', detail: 'ยอดชำระรายวันตามปฏิทิน Asia/Bangkok' },
  { id: 'checkin_orders', label: 'เช็กอินแล้ว', detail: 'แสดงจำนวนออเดอร์ที่เช็กอิน / ออเดอร์ที่ชำระแล้ว' },
];

function chartSections(text: string): { title: string; rows: { label: string; amount: number }[] }[] {
  const sections: { title: string; rows: { label: string; amount: number }[] }[] = [];
  let current: typeof sections[number] | null = null;
  for (const line of text.split('\n')) {
    if (line === 'รายได้แยกตามประเภท (หลังเฉลี่ยส่วนลด)' || line === 'แนวโน้มรายได้ 7 วัน') {
      current = { title: line, rows: [] };
      sections.push(current);
      continue;
    }
    if (!current) continue;
    const match = /^(.*?):\s*฿([\d,]+)/.exec(line);
    if (!match) continue;
    current.rows.push({ label: match[1]!, amount: Number(match[2]!.replaceAll(',', '')) });
  }
  return sections.filter((section) => section.rows.length > 0);
}

export function LineSalesReportPanel({ token, eventSlug, connection, permitted }: {
  token: string;
  eventSlug?: string;
  connection: LineOaConnectionDto;
  permitted: boolean;
}) {
  const [report, setReport] = useState<LineSalesReportDto | null>(null);
  const [fields, setFields] = useState<LineSalesReportField[]>(['revenue_total', 'revenue_today', 'orders_today']);
  const [times, setTimes] = useState<string[]>(['20:00']);
  const [enabled, setEnabled] = useState(false);
  const [pairingCode, setPairingCode] = useState<{ id: string; code: string; expiresAt: string } | null>(null);
  const [preview, setPreview] = useState<LineSalesReportPreviewDto | null>(null);
  const [usage, setUsage] = useState<LineSalesReportUsageDto | null>(null);
  const [usageConfirmed, setUsageConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async (preserveDraft = false) => {
    if (!token) return;
    const next = await lineOaApi.reports(token, eventSlug);
    setReport(next);
    if (!preserveDraft) {
      setFields(next.fields);
      setTimes(next.times.length ? next.times : ['20:00']);
      setEnabled(next.enabled);
    }
  }, [token, eventSlug]);

  useEffect(() => {
    if (!permitted || !token) return;
    void load().catch((e) => setError(e instanceof ApiError ? e.message : 'โหลดการตั้งค่ารายงานไม่สำเร็จ'));
  }, [load, permitted, token, connection.status]);

  const run = async (operation: () => Promise<unknown>, success?: string, preserveDraft = false) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await operation();
      await load(preserveDraft);
      if (success) setNotice(success);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'ทำรายการไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const dirty = useMemo(() => !!report && (
    enabled !== report.enabled
    || JSON.stringify(fields) !== JSON.stringify(report.fields)
    || JSON.stringify(times) !== JSON.stringify(report.times)
  ), [enabled, fields, report, times]);

  const startPairing = () => {
    void run(async () => {
      const result = await lineOaApi.startReportPairing(token, eventSlug);
      // The clear pairing code is returned once. Only its keyed hash is stored server-side.
      setPairingCode(result);
    }, 'สร้างรหัสจับคู่แล้ว รหัสหมดอายุใน 10 นาที');
  };

  const doPreview = () => {
    void run(async () => setPreview(await lineOaApi.previewReport(token, fields, eventSlug)), undefined, true);
  };

  const checkUsage = () => {
    void run(async () => {
      const next = await lineOaApi.reportUsage(token, times, eventSlug);
      setUsage(next);
      setUsageConfirmed(false);
    }, undefined, true);
  };

  const doTestSend = () => {
    void run(async () => {
      const result = await lineOaApi.testReport(token, eventSlug);
      setNotice(result.delivery === 'sent' ? 'LINE รับข้อความทดสอบแล้ว' : `ผลส่งข้อความทดสอบ: ${result.delivery}`);
    }, undefined, true);
  };

  const charts = preview ? chartSections(preview.text) : [];
  const maxChartValue = Math.max(1, ...charts.flatMap((section) => section.rows.map((row) => row.amount)));
  const connected = report?.group?.status === 'active';
  const messagingReady = connection.status === 'active' && connection.capability === 'messaging';
  const usageMatchesGroup = !!usage && usage.groupIdSuffix === report?.group?.groupIdSuffix;
  const estimatedMonthlyMessages = usageMatchesGroup ? usage.memberCount * times.length * 30 : null;
  const usageReady = usageMatchesGroup && usageConfirmed && usage.sendsPerDay === times.length;

  return (
    <Card title="รายงานยอดขายเข้า LINE group" sub="แยกจากการส่งตั๋ว LINE — ผู้จัดยืนยันกลุ่มก่อนเปิดตารางส่ง">
      {!report?.featureEnabled && (
        <Alert tone="warning" title="ฟีเจอร์ยังปิดใน environment นี้">ดูและเตรียมตัวอย่างได้ แต่ยังจับคู่กลุ่มหรือส่งรายงานไม่ได้</Alert>
      )}
      {!messagingReady && (
        <Alert tone="warning" title="LINE OA ยังไม่พร้อมส่ง">
          ต้องใช้ Messaging API และตรวจสอบ connection ให้เป็นสถานะใช้งานก่อน จึงจะเพิ่มบอทและจับคู่กลุ่มได้
        </Alert>
      )}
      {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}
      {notice && <Alert tone="success" title="เรียบร้อย">{notice}</Alert>}

      <section aria-labelledby="jt-line-report-pairing">
        <h3 id="jt-line-report-pairing">1. เพิ่มบอทและยืนยันกลุ่ม</h3>
        <p className="jt-field-hint">
          สร้างรหัสชั่วคราว → เปิด Allow bot to join group chats ใน LINE Developers Console → เพิ่ม OA นี้เข้ากลุ่ม
          → ส่ง <strong>เชื่อม JT-XXXXXXXX</strong> ในกลุ่ม → กลับมากดยืนยัน
          ข้อความในกลุ่มอย่างเดียวไม่เปิดเผยยอดขาย
        </p>
        {connected ? (
          <div className="jt-inline-actions">
            <span className="jt-field-hint">เชื่อมแล้ว · group ID ลงท้าย {report?.group?.groupIdSuffix}</span>
            <Button size="sm" variant="destructive" disabled={busy || !permitted} onClick={() => void run(
              () => lineOaApi.unlinkReportGroup(token, eventSlug), 'ยกเลิกกลุ่มแล้ว ตารางส่งถูกพักไว้',
            )}>ยกเลิกการเชื่อมกลุ่ม</Button>
          </div>
        ) : (
          <div className="jt-inline-actions">
            <Button size="sm" variant="secondary" disabled={busy || !permitted || !report?.featureEnabled || !messagingReady} onClick={startPairing}>
              สร้างรหัสจับคู่
            </Button>
            {pairingCode && <code className="jt-mono" aria-live="polite">{pairingCode.code}</code>}
            {pairingCode && <span className="jt-field-hint">หมดอายุ {new Date(pairingCode.expiresAt).toLocaleTimeString('th-TH')}</span>}
          </div>
        )}
        {report?.pendingPairing && !connected && (
          <div className="jt-preview-box">
            <p>พบข้อความจับคู่จากกลุ่มที่มี ID ลงท้าย <strong>{report.pendingPairing.pending_group_id.slice(-6)}</strong></p>
            <p className="jt-field-hint">ตรวจว่ากลุ่มนี้ถูกต้อง ก่อนอนุญาตให้ส่งรายงานยอดขาย</p>
            <Button size="sm" variant="primary" disabled={busy || !permitted} onClick={() => void run(
              () => lineOaApi.confirmReportPairing(token, report.pendingPairing!.id, eventSlug), 'ยืนยันกลุ่มแล้ว',
            )}>ยืนยันกลุ่มนี้</Button>
          </div>
        )}
        {report?.group && report.group.status === 'left' && (
          <Alert tone="warning" title="บอทออกจากกลุ่มแล้ว">ตารางส่งถูกพักแล้ว เพิ่มบอทกลับเข้ากลุ่มและจับคู่ใหม่เพื่อเริ่มต่อ</Alert>
        )}
      </section>

      <section aria-labelledby="jt-line-report-schedule">
        <h3 id="jt-line-report-schedule">2. ตั้งเวลาและความถี่</h3>
        <div className="jt-field-grid">
          {times.map((time, index) => (
            <TextField key={index} label={`เวลาส่งครั้งที่ ${index + 1} · Asia/Bangkok`} type="time" value={time}
              disabled={busy || !permitted} onChange={(e) => setTimes(times.map((old, i) => i === index ? e.target.value : old))} />
          ))}
        </div>
        <div className="jt-inline-actions">
          <Button size="sm" variant="ghost" disabled={busy || !permitted || times.length >= 2} onClick={() => {
            setTimes([...times, times.includes('20:00') ? '12:00' : '20:00'].sort());
            setUsageConfirmed(false);
          }}>
            เพิ่มรอบที่สอง
          </Button>
          {times.length > 1 && <Button size="sm" variant="ghost" disabled={busy || !permitted} onClick={() => { setTimes([times[0]!]); setUsageConfirmed(false); }}>เหลือวันละครั้ง</Button>}
        </div>
        <p className="jt-field-hint">รอบแรกเริ่มต้น 20:00 น. ระบบส่งตามเวลาที่ตั้งไว้ และเก็บเวลาเป็น Asia/Bangkok</p>
      </section>

      <section aria-labelledby="jt-line-report-fields">
        <h3 id="jt-line-report-fields">3. เลือกข้อมูลในรายงาน</h3>
        {FIELD_OPTIONS.map((option) => (
          <Toggle key={option.id} label={option.label} consequence={option.detail}
            checked={fields.includes(option.id)} disabled={busy || !permitted}
            onChange={(e) => setFields(e.target.checked
              ? [...fields, option.id]
              : fields.filter((field) => field !== option.id))} />
        ))}
        <div className="jt-inline-actions">
          <Button size="sm" variant="secondary" disabled={busy || !permitted || !report?.featureEnabled || !connected || !messagingReady} onClick={checkUsage}>
            ตรวจสมาชิกและคำนวณประมาณการ
          </Button>
          <Button size="sm" variant="secondary" disabled={busy || !permitted || fields.length === 0} onClick={doPreview}>
            ดูตัวอย่างข้อความและกราฟ
          </Button>
          <Button size="sm" variant="secondary" disabled={busy || !permitted || !report?.featureEnabled || !connected || !messagingReady} onClick={doTestSend}>
            ส่งทดสอบเข้ากลุ่ม
          </Button>
        </div>
        {preview && (
          <div className="jt-preview-box" aria-label="ตัวอย่าง Flex Message">
            <p className="jt-field-hint">ตัวอย่าง Flex Message · คำนวณจากข้อมูลปัจจุบัน</p>
            <pre style={{ whiteSpace: 'pre-wrap', font: 'inherit', margin: 0 }}>{preview.text}</pre>
            {charts.map((section) => (
              <div key={section.title} style={{ marginTop: 'var(--jt-space-3)' }}>
                <p className="jt-field-hint">{section.title}</p>
                {section.rows.map((row) => (
                  <div key={row.label} style={{ display: 'grid', gridTemplateColumns: 'minmax(7rem, 1fr) 2fr auto', gap: 'var(--jt-space-2)', alignItems: 'center', margin: 'var(--jt-space-1) 0' }}>
                    <span>{row.label}</span>
                    <span aria-hidden="true" style={{ display: 'block', height: 8, borderRadius: 99, width: `${Math.max(3, Math.round(row.amount / maxChartValue * 100))}%`, background: 'var(--jt-color-accent, #4D8B79)' }} />
                    <strong>{`฿${row.amount.toLocaleString('en-US')}`}</strong>
                  </div>
                ))}
              </div>
            ))}
            <p className="jt-field-hint">ข้อความและกราฟใน Flex ใช้ snapshot เดียวกัน</p>
          </div>
        )}
        {usageMatchesGroup && (
          <div className="jt-preview-box" aria-live="polite">
            <p><strong>กลุ่มนี้มีสมาชิก {usage.memberCount.toLocaleString('th-TH')} คน</strong></p>
            <p>ประมาณ {estimatedMonthlyMessages?.toLocaleString('th-TH')} ข้อความตามผู้รับต่อ 30 วัน
              ({usage.memberCount.toLocaleString('th-TH')} คน × {times.length} ครั้ง/วัน × 30 วัน)</p>
            <p className="jt-field-hint">
              เป็นประมาณการแบบเผื่อสูงก่อนหักสมาชิกที่บล็อกหรือรับข้อความไม่ได้ ไม่ใช่ยอดโควต้าคงเหลือ
              และจำนวนจริงขึ้นกับผู้รับที่ LINE ส่งถึง ตรวจแพ็กเกจและยอดใช้จริงใน LINE Official Account Manager
            </p>
            {enabled && <Toggle label="ฉันตรวจสอบประมาณการนี้แล้ว" checked={usageReady}
              disabled={busy || !permitted || usage.sendsPerDay !== times.length}
              consequence="ใช้จำนวนสมาชิกจริงของกลุ่มนี้และความถี่ที่เลือก ก่อนเปิดตารางส่ง"
              onChange={(e) => setUsageConfirmed(e.target.checked)} />}
          </div>
        )}
      </section>

      <section aria-labelledby="jt-line-report-activation">
        <h3 id="jt-line-report-activation">4. เปิดหรือพักรายงาน</h3>
        <Toggle label="เปิดส่งรายงานตามเวลา" checked={enabled}
          disabled={busy || !permitted || !report?.featureEnabled || !connected}
          consequence={connected ? 'ส่งเฉพาะยอดชำระแล้วไปยังกลุ่มที่ยืนยันไว้' : 'ต้องยืนยัน LINE group ก่อนเปิด'}
          onChange={(e) => setEnabled(e.target.checked)} />
        <div className="jt-inline-actions">
          <Button variant="primary" size="sm" disabled={busy || !permitted || !dirty || fields.length === 0 || (enabled && !usageReady)
            || new Set(times).size !== times.length || times.some((time) => !/^([01]\d|2[0-3]):[0-5]\d$/.test(time))}
            onClick={() => void run(() => lineOaApi.saveReports(token, {
              enabled, fields, times, ...(enabled && usage ? {
                acknowledgedMemberCount: usage.memberCount,
                acknowledgedGroupFingerprint: usage.groupFingerprint,
                acknowledgedSendsPerDay: usage.sendsPerDay,
              } : {}),
            }, eventSlug), 'บันทึกการตั้งค่ารายงานแล้ว')}>
            บันทึกการตั้งค่า
          </Button>
          <span className="jt-field-hint">{enabled ? 'เปิดอยู่' : 'พักอยู่'}{report?.nextRuns[0] ? ` · รอบถัดไป ${new Intl.DateTimeFormat('th-TH', { timeZone: 'Asia/Bangkok', dateStyle: 'medium', timeStyle: 'short' }).format(new Date(report.nextRuns[0]))} น.` : ''}</span>
        </div>
      </section>

      <section aria-labelledby="jt-line-report-history">
        <h3 id="jt-line-report-history">ประวัติส่งล่าสุด</h3>
        {report?.history.length ? (
          <ul>
            {report.history.slice(0, 5).map((item) => (
              <li key={item.id}>{new Date(item.scheduled_for).toLocaleString('th-TH')} · {item.status}{item.last_error ? ` · ${item.last_error}` : ''}</li>
            ))}
          </ul>
        ) : <p className="jt-field-hint">ยังไม่มีประวัติการส่ง</p>}
      </section>
    </Card>
  );
}
